import { useCallback, useEffect, useRef, useState } from 'react';
import { estVideo, type MediaPublic } from '../types';
import { Croix } from './Icones';

interface Props {
  medias: MediaPublic[];
  indexInitial: number;
  onFermer: () => void;
}

/** Distance minimale d'un balayage pour être pris pour une intention, en pixels. */
const SEUIL_BALAYAGE = 50;

export function Visionneuse({ medias, indexInitial, onFermer }: Props) {
  const [index, setIndex] = useState(indexInitial);
  const conteneur = useRef<HTMLDivElement>(null);
  const departToucher = useRef<{ x: number; y: number } | null>(null);

  const media = medias[index];

  const precedent = useCallback(
    () => setIndex((i) => (i - 1 + medias.length) % medias.length),
    [medias.length],
  );
  const suivant = useCallback(() => setIndex((i) => (i + 1) % medias.length), [medias.length]);

  // ── Clavier ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent) => {
      switch (evenement.key) {
        case 'Escape':
          onFermer();
          break;
        case 'ArrowLeft':
          precedent();
          break;
        case 'ArrowRight':
          suivant();
          break;
      }
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [onFermer, precedent, suivant]);

  // ── Défilement de la page bloqué pendant l'ouverture ───────────────────────
  // Sans cela, un balayage vertical fait défiler la galerie derrière la visionneuse,
  // et on la retrouve ailleurs en fermant.
  useEffect(() => {
    const precedente = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = precedente;
    };
  }, []);

  // Le focus part sur la visionneuse : sinon les touches continuent d'agir sur la
  // grille en arrière-plan, et le lecteur d'écran reste sur la page précédente.
  useEffect(() => {
    conteneur.current?.focus();
  }, []);

  /**
   * Précharge la vignette de l'œuvre suivante.
   *
   * On précharge la vignette et non le fichier : sur une galerie de vidéos, tirer
   * d'avance 85 Mo pour une œuvre que le visiteur ne regardera peut-être pas
   * coûterait plus cher que l'attente qu'on cherche à éviter.
   */
  useEffect(() => {
    const suivante = medias[(index + 1) % medias.length];
    if (!suivante?.urlVignette) return;
    const image = new Image();
    image.src = suivante.urlVignette;
  }, [index, medias]);

  if (!media) return null;

  return (
    <div
      ref={conteneur}
      className="visionneuse"
      role="dialog"
      aria-modal="true"
      aria-label={media.libelle}
      tabIndex={-1}
      onTouchStart={(e) => {
        const t = e.touches[0];
        if (t) departToucher.current = { x: t.clientX, y: t.clientY };
      }}
      onTouchEnd={(e) => {
        const depart = departToucher.current;
        const fin = e.changedTouches[0];
        if (!depart || !fin) return;
        departToucher.current = null;

        const dx = fin.clientX - depart.x;
        const dy = fin.clientY - depart.y;

        // Un balayage plus vertical qu'horizontal n'est pas une navigation : c'est
        // souvent le début d'un défilement, ou la barre du navigateur qu'on tire.
        if (Math.abs(dx) < SEUIL_BALAYAGE || Math.abs(dy) > Math.abs(dx)) return;
        if (dx > 0) precedent();
        else suivant();
      }}
    >
      <div className="visionneuse-barre">
        <span className="visionneuse-compteur">
          {index + 1} / {medias.length}
        </span>
        <button className="visionneuse-bouton croix" onClick={onFermer} aria-label="Fermer">
          <Croix />
        </button>
      </div>

      {medias.length > 1 && (
        <button className="fleche precedente" onClick={precedent} aria-label="Œuvre précédente">
          ‹
        </button>
      )}

      <figure className="visionneuse-contenu">
        {estVideo(media) ? (
          // `key` force le remplacement de l'élément d'une œuvre à l'autre : sans lui,
          // React réutilise la même balise et la vidéo précédente continue de jouer
          // sous la nouvelle source.
          <video
            key={media.cle}
            src={media.url}
            controls
            autoPlay
            playsInline
            preload="metadata"
          />
        ) : media.type === 'application/pdf' ? (
          <a className="lien-document" href={media.url} target="_blank" rel="noreferrer noopener">
            Ouvrir le document
          </a>
        ) : (
          <img key={media.cle} src={media.url} alt={media.libelle} />
        )}

        {/*
          PAS de libellé affiché. Ce champ contient le nom du fichier d'origine —
          « Activit_01_MTN », « danseuse4_700x » — pas un titre d'œuvre. L'afficher
          sous une image donnait à lire un nom de fichier au visiteur.
          Seule une description saisie à la main mérite d'apparaître.
        */}
        {media.description && (
          <figcaption className="visionneuse-legende">{media.description}</figcaption>
        )}
      </figure>

      {medias.length > 1 && (
        <button className="fleche suivante" onClick={suivant} aria-label="Œuvre suivante">
          ›
        </button>
      )}
    </div>
  );
}
